import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import toast from 'react-hot-toast';
import {
  Video,
  Play,
  Trash2,
  Copy,
  RefreshCw,
  X,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Info,
  Clock,
} from 'lucide-react';

import {
  useGetS3VideosQuery,
  useDeleteVideoMutation,
  VideoData,
} from '@/store/api/videoGenerationsApi';

// Format bytes into human-readable format
const formatBytes = (bytes: number, decimals = 2) => {
  if (!bytes || bytes === 0) return '0 Bytes';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

// Confirmation modal for single video deletion
function ConfirmDeleteVideoDialog({
  videoTitle,
  onConfirm,
  onCancel,
  isLoading,
}: {
  videoTitle: string;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading: boolean;
}) {
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/45 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden animate-in zoom-in-95 duration-200 border border-gray-105">
        <div className="bg-[linear-gradient(to_right,#082261,#34065c)] p-5 text-white flex items-center gap-3">
          <div className="bg-white/20 rounded-full p-2">
            <Trash2 className="h-5 w-5" />
          </div>
          <div>
            <p className="font-semibold font-spectral">Confirm Delete</p>
            <p className="text-white/70 text-xs mt-0.5">This action cannot be undone</p>
          </div>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-sm text-gray-600">
            Are you sure you want to permanently delete the video <strong>"{videoTitle}"</strong>?
          </p>
          <div className="flex gap-3 justify-end">
            <button
              onClick={onCancel}
              disabled={isLoading}
              className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={isLoading}
              className="px-4 py-2 text-sm rounded-lg bg-red-700 text-white font-semibold hover:bg-red-650 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {isLoading ? (
                <>
                  <svg
                    className="animate-spin h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Deleting...
                </>
              ) : (
                'Delete Video'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Confirmation modal for bulk video deletion
function ConfirmBulkDeleteDialog({
  selectedCount,
  onConfirm,
  onCancel,
  isLoading,
  progress,
}: {
  selectedCount: number;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading: boolean;
  progress?: { current: number; total: number };
}) {
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/45 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden animate-in zoom-in-95 duration-200 border border-gray-105">
        <div className="bg-gradient-to-r from-red-800 to-red-950 p-5 text-white flex items-center gap-3">
          <div className="bg-white/20 rounded-full p-2">
            <Trash2 className="h-5 w-5" />
          </div>
          <div>
            <p className="font-semibold font-spectral">Confirm Bulk Delete</p>
            <p className="text-white/70 text-xs mt-0.5">This action is permanent</p>
          </div>
        </div>
        <div className="p-5 space-y-4">
          {isLoading && progress ? (
            <div className="space-y-2.5 py-2">
              <div className="flex justify-between items-center text-xs font-semibold text-gray-500">
                <span>Deleting Videos...</span>
                <span>
                  {progress.current} of {progress.total}
                </span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                <div
                  className="bg-red-650 h-2 bg-red-600 rounded-full transition-all duration-300"
                  style={{ width: `${(progress.current / progress.total) * 100}%` }}
                />
              </div>
              <p className="text-xs text-gray-400 italic text-center animate-pulse">
                Please do not close this tab. Processing request...
              </p>
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-600">
                Are you sure you want to permanently delete the <strong>{selectedCount}</strong>{' '}
                selected videos from both the database and S3 cloud storage?
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={onCancel}
                  disabled={isLoading}
                  className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={onConfirm}
                  disabled={isLoading}
                  className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white font-semibold hover:bg-red-500 transition-colors disabled:opacity-50"
                >
                  Delete All Selected
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function S3VideosPage() {
  // Pagination State
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  // Video Actions State
  const [selectedVideo, setSelectedVideo] = useState<VideoData | null>(null);
  const [videoToDelete, setVideoToDelete] = useState<VideoData | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  const [isDeletingBulk, setIsDeletingBulk] = useState(false);
  const [bulkDeleteProgress, setBulkDeleteProgress] = useState({ current: 0, total: 0 });

  // API Queries & Mutations
  const skip = (page - 1) * limit;
  const { data, isLoading, isFetching, refetch } = useGetS3VideosQuery({ skip, take: limit });
  const [deleteVideo, { isLoading: isDeleting }] = useDeleteVideoMutation();

  // Selected item counts
  const videosList = data?.data || [];
  const totalCount = data?.count || 0;
  const totalPages = Math.ceil(totalCount / limit) || 1;

  // Selected IDs helpers
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const pageIds = videosList.map((v) => v.id);
      setSelectedIds((prev) => Array.from(new Set([...prev, ...pageIds])));
    } else {
      const pageIds = videosList.map((v) => v.id);
      setSelectedIds((prev) => prev.filter((id) => !pageIds.includes(id)));
    }
  };

  const handleSelectOne = (id: string, checked: boolean) => {
    if (checked) {
      setSelectedIds((prev) => [...prev, id]);
    } else {
      setSelectedIds((prev) => prev.filter((x) => x !== id));
    }
  };

  // Check if all items on current page are selected
  const isAllCurrentSelected =
    videosList.length > 0 && videosList.every((v) => selectedIds.includes(v.id));

  // Refresh handler
  const handleRefresh = async () => {
    try {
      await refetch().unwrap();
      toast.success('S3 video directory reloaded');
    } catch {
      toast.error('Failed to reload S3 video directory');
    }
  };

  // Copy helper
  const handleCopyUrl = (url: string) => {
    navigator.clipboard.writeText(url);
    toast.success('S3 Video URL copied to clipboard');
  };

  // Delete handlers
  const handleDeleteConfirm = async () => {
    if (!videoToDelete) return;
    try {
      await deleteVideo(videoToDelete.id).unwrap();
      toast.success('Video deleted successfully');
      setSelectedIds((prev) => prev.filter((id) => id !== videoToDelete.id));
      setVideoToDelete(null);
    } catch (err: any) {
      const errMsg = err?.data?.message || err?.message || 'Failed to delete video';
      toast.error(errMsg);
    }
  };

  const handleBulkDeleteConfirm = async () => {
    if (selectedIds.length === 0) return;
    setIsDeletingBulk(true);
    setBulkDeleteProgress({ current: 0, total: selectedIds.length });

    let deletedCount = 0;
    try {
      for (let i = 0; i < selectedIds.length; i++) {
        setBulkDeleteProgress({ current: i + 1, total: selectedIds.length });
        const videoId = selectedIds[i];
        if (videoId) {
          await deleteVideo(videoId).unwrap();
          deletedCount++;
        }
      }
      toast.success(`Successfully deleted ${deletedCount} videos`);
      setSelectedIds([]);
      setShowBulkDelete(false);
    } catch (err: any) {
      const errMsg = err?.data?.message || err?.message || 'Failed during bulk deletion';
      toast.error(`${errMsg} (Deleted ${deletedCount} of ${selectedIds.length})`);
    } finally {
      setIsDeletingBulk(false);
      setBulkDeleteProgress({ current: 0, total: 0 });
    }
  };

  return (
    <div className="space-y-6 w-full mx-auto pb-12">
      {/* Title Header Card */}
      <div className="bg-[linear-gradient(to_right,#082261,#34065c)] rounded-2xl shadow-xl p-6 md:p-8 text-white relative overflow-hidden border border-white/10">
        <div className="absolute top-0 right-0 p-4 opacity-10">
          <Video className="w-40 h-40" />
        </div>

        <div className="relative z-10 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="bg-white/10 backdrop-blur-md text-xs font-semibold px-2.5 py-1 rounded-full text-indigo-200 border border-white/15 flex items-center gap-1">
                <Sparkles className="w-3.5 h-3.5 text-yellow-300 animate-pulse" />
                S3 Directory Live
              </span>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold font-spectral tracking-tight bg-clip-text bg-gradient-to-r from-white to-indigo-100">
              S3 Uploaded Videos
            </h1>
            <p className="text-indigo-100/70 text-sm md:text-base max-w-xl">
              Browse all generated videos directly from your S3 cloud bucket. Preview file content,
              copy S3 URLs, and perform single or bulk-deletion operations.
            </p>
          </div>

          <button
            onClick={handleRefresh}
            disabled={isLoading || isFetching}
            className="flex items-center justify-center gap-2 px-4 py-2 text-sm font-semibold rounded-xl bg-white/10 border border-white/15 text-indigo-100 hover:bg-white/20 transition-all shrink-0 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading || isFetching ? 'animate-spin' : ''}`} />
            Refresh Directory
          </button>
        </div>
      </div>

      {/* Main Table Grid Card */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-md overflow-hidden">
        {/* Bulk Action Header Panel */}
        {selectedIds.length > 0 && (
          <div className="bg-indigo-50/70 border-b border-indigo-100 px-6 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 animate-in slide-in-from-top-4 duration-300">
            <div className="flex items-center gap-2 text-indigo-900 font-semibold text-sm">
              <div className="bg-indigo-600 text-white rounded-lg px-2 py-0.5 text-xs font-mono">
                {selectedIds.length}
              </div>
              <span>videos selected for bulk action</span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setSelectedIds([])}
                className="px-3.5 py-1.5 text-xs font-semibold rounded-lg border border-gray-300 text-gray-600 bg-white hover:bg-gray-50 transition-colors"
              >
                Clear Selection
              </button>
              <button
                onClick={() => setShowBulkDelete(true)}
                className="px-3.5 py-1.5 text-xs font-semibold rounded-lg bg-red-600 text-white hover:bg-red-500 transition-colors flex items-center gap-1.5 shadow-md shadow-red-500/10"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Delete Selected ({selectedIds.length})
              </button>
            </div>
          </div>
        )}

        {/* Content Box */}
        {isLoading ? (
          <div className="py-24 text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#082261] mx-auto mb-4" />
            <p className="text-gray-500 text-sm">Loading S3 directory...</p>
          </div>
        ) : videosList.length === 0 ? (
          <div className="py-20 text-center max-w-sm mx-auto space-y-3">
            <div className="bg-gray-50 rounded-full p-4 w-14 h-14 flex items-center justify-center mx-auto text-gray-400">
              <Video className="w-6 h-6" />
            </div>
            <h3 className="font-bold text-gray-700">No Videos Found</h3>
            <p className="text-gray-500 text-xs leading-relaxed">
              No video database records exist in S3. Use the S3 Upload tool to transfer MP4 binaries
              and register them.
            </p>
            <button
              onClick={handleRefresh}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-indigo-50 text-indigo-650 hover:bg-indigo-100 transition-colors"
            >
              <RefreshCw className="w-3 h-3" /> Retry Loading
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto relative">
            {isFetching && (
              <div className="absolute inset-0 bg-white/50 z-10 flex items-center justify-center backdrop-blur-[0.5px]">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#082261]" />
              </div>
            )}
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50/75 border-b border-gray-100 text-gray-650 text-xs font-bold uppercase tracking-wider">
                  <th className="py-4 px-6 w-12 text-center">
                    <input
                      type="checkbox"
                      checked={isAllCurrentSelected}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500/30 h-4 w-4 cursor-pointer"
                    />
                  </th>
                  <th className="py-4 px-4 font-semibold">Video Details</th>
                  <th className="py-4 px-4 font-semibold">S3 Key Path</th>
                  <th className="py-4 px-4 font-semibold">File Size</th>
                  <th className="py-4 px-4 font-semibold">Created At</th>
                  <th className="py-4 px-4 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 text-sm text-gray-700">
                {videosList.map((video) => {
                  const isSelected = selectedIds.includes(video.id);
                  return (
                    <tr
                      key={video.id}
                      className={`hover:bg-gray-50/50 transition-colors ${isSelected ? 'bg-indigo-50/20' : ''}`}
                    >
                      <td className="py-4 px-6 text-center">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => handleSelectOne(video.id, e.target.checked)}
                          className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500/30 h-4 w-4 cursor-pointer"
                        />
                      </td>
                      <td className="py-4 px-4">
                        <div className="font-semibold text-gray-900 leading-tight">
                          {video.title || 'Untitled Video'}
                        </div>
                        <div className="text-[10px] text-gray-400 font-mono mt-0.5 max-w-[200px] truncate">
                          {video.id}
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div
                          className="font-mono text-xs text-gray-500 truncate max-w-[250px]"
                          title={video.s3Key}
                        >
                          {video.s3Key}
                        </div>
                      </td>
                      <td className="py-4 px-4 text-gray-650 font-medium">
                        {formatBytes(video.fileSize)}
                      </td>
                      <td className="py-4 px-4 text-gray-550 flex items-center gap-1.5 mt-2">
                        <Clock className="w-3.5 h-3.5 text-gray-400" />
                        <span className="text-xs">{video.createdAt}</span>
                      </td>
                      <td className="py-4 px-4 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            onClick={() => setSelectedVideo(video)}
                            className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                            title="Play video"
                          >
                            <Play className="w-4 h-4 fill-indigo-600" />
                          </button>
                          <button
                            onClick={() => handleCopyUrl(video.videoUrl)}
                            className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                            title="Copy S3 URL"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setVideoToDelete(video)}
                            className="p-1.5 text-red-650 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete video"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Footer Pagination Controls */}
        {!isLoading && videosList.length > 0 && (
          <div className="bg-gray-50/50 border-t border-gray-100 px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4 text-xs text-gray-500">
              <div className="flex items-center gap-1.5">
                <span>Show</span>
                <select
                  value={limit}
                  onChange={(e) => {
                    setLimit(Number(e.target.value));
                    setPage(1);
                  }}
                  className="px-2 py-1 rounded border border-gray-300 text-gray-700 bg-white text-xs"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
                <span>items</span>
              </div>
              <span className="h-4 border-r border-gray-300" />
              <span>
                Showing <strong>{skip + 1}</strong> to{' '}
                <strong>{Math.min(skip + limit, totalCount)}</strong> of{' '}
                <strong>{totalCount}</strong> items
              </span>
            </div>

            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-lg disabled:opacity-50 transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              {Array.from({ length: totalPages }).map((_, idx) => {
                const pNum = idx + 1;
                const isCurrent = page === pNum;
                return (
                  <button
                    key={pNum}
                    onClick={() => setPage(pNum)}
                    className={`w-7 h-7 text-xs font-semibold rounded-lg transition-colors ${
                      isCurrent ? 'bg-[#082261] text-white' : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {pNum}
                  </button>
                );
              })}

              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-lg disabled:opacity-50 transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Video Player Modal portal */}
      {selectedVideo &&
        createPortal(
          <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200">
            <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden max-w-3xl w-full shadow-2xl relative">
              <div className="p-4 border-b border-gray-800 bg-gray-950 text-white flex items-center justify-between">
                <div>
                  <h3 className="font-bold text-sm font-spectral">{selectedVideo.title}</h3>
                  <p className="text-[10px] text-gray-500 font-mono mt-0.5 truncate max-w-xs md:max-w-md">
                    S3 Link: {selectedVideo.videoUrl}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedVideo(null)}
                  className="p-1 text-gray-400 hover:text-white rounded-lg hover:bg-gray-850"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="bg-black aspect-video flex items-center justify-center relative">
                <video
                  src={selectedVideo.videoUrl}
                  controls
                  autoPlay
                  className="w-full h-full object-contain"
                />
              </div>

              <div className="p-4 bg-gray-950 border-t border-gray-800 grid grid-cols-2 md:grid-cols-4 gap-4 text-xs text-gray-400">
                <div>
                  <span className="text-gray-500 block">Database ID</span>
                  <span
                    className="font-mono text-gray-300 font-semibold truncate block"
                    title={selectedVideo.id}
                  >
                    {selectedVideo.id}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500 block">File Size</span>
                  <span className="text-gray-350 font-semibold block">
                    {formatBytes(selectedVideo.fileSize)}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500 block">Folder Path</span>
                  <span
                    className="text-gray-350 font-mono block truncate"
                    title={selectedVideo.s3Key}
                  >
                    {selectedVideo.s3Key.substring(0, selectedVideo.s3Key.indexOf('/')) || 'N/A'}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500 block">Created At</span>
                  <span className="text-gray-350 block">{selectedVideo.createdAt}</span>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* Delete Video Dialog portal */}
      {videoToDelete &&
        createPortal(
          <ConfirmDeleteVideoDialog
            videoTitle={videoToDelete.title || 'Untitled Video'}
            onConfirm={handleDeleteConfirm}
            onCancel={() => setVideoToDelete(null)}
            isLoading={isDeleting}
          />,
          document.body
        )}

      {/* Bulk Delete Dialog portal */}
      {showBulkDelete &&
        createPortal(
          <ConfirmBulkDeleteDialog
            selectedCount={selectedIds.length}
            onConfirm={handleBulkDeleteConfirm}
            onCancel={() => setShowBulkDelete(false)}
            isLoading={isDeletingBulk}
            progress={bulkDeleteProgress}
          />,
          document.body
        )}
    </div>
  );
}
